import React, { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { makeApiCall, makeJsonApiCall } from '../utils/customerUtils'
import { buildApiUrl } from '../utils/api'
import { useTranslation } from 'react-i18next'

interface SecurityVerificationModalProps {
  isOpen: boolean
  onVerified: (token?: string, refreshToken?: string, user?: any, verificationMethod?: 'passkey' | 'sms_2fa' | 'totp_2fa') => void
  onSkip?: () => void
  onCancel?: () => void  // Cancel login during verification
  userEmail: string
  sessionToken?: string  // Pending auth session token from backend
  has2FA?: boolean  // From backend response
  hasSMS2FA?: boolean  // From backend response
  hasPasskeys?: boolean  // From backend response
  preferredMethod?: 'passkey' | 'sms_2fa' | 'totp_2fa' | null  // User's quick login preference
}

const SecurityVerificationModal: React.FC<SecurityVerificationModalProps> = ({
  isOpen,
  onVerified,
  onSkip,
  onCancel,
  userEmail,
  sessionToken,
  has2FA: has2FAFromBackend,
  hasSMS2FA: hasSMS2FAFromBackend,
  hasPasskeys: hasPasskeysFromBackend,
  preferredMethod
}) => {
  const { t } = useTranslation()
  const { registerPasskey, getPasskeys, supportsPasskeys } = useAuth()
  
  // State for checking what security features are available
  const [loading, setLoading] = useState(true)
  const [hasPasskeys, setHasPasskeys] = useState(false)
  const [has2FA, setHas2FA] = useState(false)
  const [hasSMS2FA, setHasSMS2FA] = useState(false)
  
  // Verification states
  const [selectedMethod, setSelectedMethod] = useState<'passkey' | '2fa' | 'sms2fa' | 'setup' | null>(null)
  const [verifying, setVerifying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // 2FA token input
  const [tfaToken, setTfaToken] = useState('')
  
  // SMS 2FA state
  const [smsCodeSent, setSmsCodeSent] = useState(false)
  const [canResendSMS, setCanResendSMS] = useState(true)
  const [resendTimer, setResendTimer] = useState(0)
  
  // Setup flow
  const [settingUp, setSettingUp] = useState(false)
  const [setupStep, setSetupStep] = useState<'choose' | 'passkey' | '2fa-setup' | '2fa-verify' | 'success'>('choose')
  const [tfaSecret, setTfaSecret] = useState('')
  const [tfaQRCode, setTfaQRCode] = useState('')
  const [tfaBackupCodes, setTfaBackupCodes] = useState<string[]>([])
  const [setupComplete, setSetupComplete] = useState(false)

  useEffect(() => {
    if (isOpen) {
      // Reset input states when modal opens
      setTfaToken('')
      setError(null)
      setSmsCodeSent(false)
      
      // If we have sessionToken, use backend-provided data (login flow)
      if (sessionToken && has2FAFromBackend !== undefined && hasSMS2FAFromBackend !== undefined && hasPasskeysFromBackend !== undefined) {
        setHas2FA(has2FAFromBackend)
        setHasSMS2FA(hasSMS2FAFromBackend)
        setHasPasskeys(hasPasskeysFromBackend)
        setLoading(false)
        
        // Auto-trigger preferred method if specified, otherwise let user choose
        if (preferredMethod === 'sms_2fa' && hasSMS2FAFromBackend) {
          setSelectedMethod('sms2fa')
          // Auto-send SMS code
          setTimeout(() => {
            handleSendSMSCode()
          }, 100)
        } else if (preferredMethod === 'totp_2fa' && has2FAFromBackend) {
          // Auto-select Auth App method and show input immediately
          setSelectedMethod('2fa')
        } else if (preferredMethod === 'passkey' && hasPasskeysFromBackend) {
          // Auto-select Passkey method
          setSelectedMethod('passkey')
        } else {
          // No preferred method, show selection screen
          setSelectedMethod(null)
        }
      } else {
        // Otherwise check features (setup flow - user already authenticated)
        setSelectedMethod(null)
        checkSecurityFeatures()
      }
    } else {
      // Clean up when modal closes
      setSelectedMethod(null)
      setTfaToken('')
      setError(null)
      setSmsCodeSent(false)
      setCanResendSMS(true)
      setResendTimer(0)
    }
  }, [isOpen, sessionToken, has2FAFromBackend, hasSMS2FAFromBackend, hasPasskeysFromBackend, preferredMethod])

  const checkSecurityFeatures = async () => {
    try {
      setLoading(true)
      
      // Check for passkeys
      if (supportsPasskeys) {
        const passkeys = await getPasskeys()
        setHasPasskeys(passkeys.length > 0)
      }
      
      // Check for 2FA
      const response = await makeApiCall('/api/2fa/status')
      if (response.ok) {
        const data = await response.json()
        setHas2FA(data.enabled)
      }
      
      // Check for SMS 2FA
      const smsResponse = await makeApiCall('/api/sms2fa/status')
      if (smsResponse.ok) {
        const data = await smsResponse.json()
        setHasSMS2FA(data.enabled)
      }
      
      setLoading(false)
    } catch (err) {
      console.error('Error checking security features:', err)
      setLoading(false)
    }
  }

  const handlePasskeyVerification = async () => {
    try {
      setVerifying(true)
      setError(null)
      
      // Trigger passkey authentication with user email for user-specific passkeys
      const challenge = await fetch(buildApiUrl('/api/auth/passkey/authenticate/begin'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: userEmail })
      })
      
      if (!challenge.ok) {
        throw new Error('Failed to start passkey verification')
      }
      
      const options = await challenge.json()
      
      // Convert challenge from base64url to ArrayBuffer
      const challengeBuffer = Uint8Array.from(atob(options.challenge.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0))
      
      // Build allowCredentials array if provided
      let allowCredentials: any[] | undefined
      if (options.allowCredentials && options.allowCredentials.length > 0) {
        allowCredentials = options.allowCredentials.map((cred: any) => {
          // Convert base64url string to Uint8Array (Safari-compatible)
          const base64url = cred.id
          const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
          const binaryString = atob(base64)
          const bytes = new Uint8Array(binaryString.length)
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i)
          }
          
          return {
            id: bytes,
            type: 'public-key' as const,
          }
        })
      }
      
      // Get credential for this specific user's passkeys
      const credential = await navigator.credentials.get({
        publicKey: {
          challenge: challengeBuffer,
          timeout: options.timeout || 60000,
          rpId: options.rpId || window.location.hostname,
          userVerification: options.userVerification || "preferred",
          ...(allowCredentials && allowCredentials.length > 0 && { allowCredentials })
        }
      }) as PublicKeyCredential
      
      if (!credential) {
        throw new Error('No passkey selected')
      }
      
      // Verify with backend
      const response = credential.response as AuthenticatorAssertionResponse
      const arrayBufferToBase64url = (buffer: ArrayBuffer) => {
        const bytes = new Uint8Array(buffer)
        const base64 = btoa(String.fromCharCode(...bytes))
        return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
      }
      
      const verifyResponse = await fetch(buildApiUrl('/api/auth/passkey/authenticate/finish'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          body: {
            id: credential.id,
            rawId: arrayBufferToBase64url(credential.rawId),
            response: {
              authenticatorData: arrayBufferToBase64url(response.authenticatorData),
              clientDataJSON: arrayBufferToBase64url(response.clientDataJSON),
              signature: arrayBufferToBase64url(response.signature),
              userHandle: response.userHandle ? arrayBufferToBase64url(response.userHandle) : null
            },
            type: credential.type
          },
          ...(sessionToken && { sessionToken })
        })
      })
      
      if (verifyResponse.ok) {
        const data = await verifyResponse.json()
        // If sessionToken was used, backend returns full auth tokens
        if (sessionToken && data.token) {
          onVerified(data.token, data.refreshToken, data.user, 'passkey')
        } else {
          onVerified()
        }
      } else {
        const errorData = await verifyResponse.json().catch(() => ({}))
        
        // Check for session expiration
        if (errorData.code === 'SESSION_EXPIRED' || errorData.error === 'Session expired') {
          setError(t('auth.sessionExpiredPleaseLogin'))
          // If onCancel is provided, call it after a delay to close modal and return to login
          if (onCancel) {
            setTimeout(() => {
              onCancel()
            }, 2000)
          }
          return
        }
        
        throw new Error(errorData.message || 'Passkey verification failed')
      }
    } catch (err: any) {
      console.error('Passkey verification error:', err)
      // Don't overwrite session expired message
      if (!error || !error.includes('session')) {
        setError(err.message || 'Failed to verify passkey')
      }
    } finally {
      setVerifying(false)
    }
  }

  const handle2FAVerification = async () => {
    try {
      setVerifying(true)
      setError(null)
      
      // Include sessionToken if this is part of login flow
      const response = await makeJsonApiCall('/api/2fa/verify', 'POST', { 
        token: tfaToken,
        ...(sessionToken && { sessionToken })
      })
      
      if (response.ok) {
        const data = await response.json()
        // If sessionToken was used, backend returns full auth tokens
        if (sessionToken && data.token) {
          onVerified(data.token, data.refreshToken, data.user, 'totp_2fa')
        } else {
          onVerified()
        }
      } else {
        const data = await response.json()
        
        // Check for session expiration
        if (data.code === 'SESSION_EXPIRED' || data.error === 'Session expired') {
          setError(t('auth.sessionExpiredPleaseLogin'))
          if (onCancel) {
            setTimeout(() => {
              onCancel()
            }, 2000)
          }
          return
        }
        
        throw new Error(data.error || 'Invalid verification code')
      }
    } catch (err: any) {
      // Don't overwrite session expired message
      if (!error || !error.includes('session')) {
        setError(err.message || 'Verification failed')
      }
    } finally {
      setVerifying(false)
    }
  }

  const handleSMS2FAVerification = async () => {
    try {
      setVerifying(true)
      setError(null)
      
      // Include sessionToken if this is part of login flow
      const response = await makeJsonApiCall('/api/sms2fa/verify', 'POST', { 
        code: tfaToken,
        ...(sessionToken && { sessionToken })
      })
      
      if (response.ok) {
        const data = await response.json()
        // If sessionToken was used, backend returns full auth tokens
        if (sessionToken && data.token) {
          onVerified(data.token, data.refreshToken, data.user, 'sms_2fa')
        } else {
          onVerified()
        }
      } else {
        const data = await response.json()
        
        // Check for session expiration
        if (data.code === 'SESSION_EXPIRED' || data.error === 'Session expired') {
          setError(t('auth.sessionExpiredPleaseLogin'))
          if (onCancel) {
            setTimeout(() => {
              onCancel()
            }, 2000)
          }
          return
        }
        
        throw new Error(data.message || data.error || 'Invalid verification code')
      }
    } catch (err: any) {
      // Don't overwrite session expired message
      if (!error || !error.includes('session')) {
        setError(err.message || 'Verification failed')
      }
    } finally {
      setVerifying(false)
    }
  }

  const handleSendSMSCode = async () => {
    try {
      setError(null)
      setVerifying(true)
      
      const response = await makeJsonApiCall('/api/sms2fa/send-login-code', 'POST', { 
        sessionToken 
      })
      
      if (response.ok) {
        setSmsCodeSent(true)
        setCanResendSMS(false)
        // Start 60 second timer before allowing resend
        setResendTimer(60)
        const interval = setInterval(() => {
          setResendTimer(prev => {
            if (prev <= 1) {
              clearInterval(interval)
              setCanResendSMS(true)
              return 0
            }
            return prev - 1
          })
        }, 1000)
      } else {
        const data = await response.json()
        throw new Error(data.message || 'Failed to send code')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send code')
      setSelectedMethod(null) // Go back to method selection
    } finally {
      setVerifying(false)
    }
  }

  const handleResendSMS = async () => {
    try {
      setError(null)
      setCanResendSMS(false)
      
      const response = await makeJsonApiCall('/api/sms2fa/resend', 'POST', { 
        sessionToken 
      })
      
      if (response.ok) {
        setSmsCodeSent(true)
        // Start 60 second timer before allowing resend
        setResendTimer(60)
        const interval = setInterval(() => {
          setResendTimer(prev => {
            if (prev <= 1) {
              clearInterval(interval)
              setCanResendSMS(true)
              return 0
            }
            return prev - 1
          })
        }, 1000)
      } else {
        const data = await response.json()
        throw new Error(data.message || 'Failed to resend code')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to resend code')
      setCanResendSMS(true)
    }
  }

  const handleSetupPasskey = async () => {
    try {
      setSettingUp(true)
      setError(null)
      await registerPasskey('Primary Security Key')
      // Show success screen instead of closing immediately
      setSetupComplete(true)
      setSetupStep('success')
      // Refresh security features
      await checkSecurityFeatures()
    } catch (err: any) {
      setError(err.message || 'Failed to setup passkey')
    } finally {
      setSettingUp(false)
    }
  }

  const handleBegin2FASetup = async () => {
    try {
      setSettingUp(true)
      setError(null)
      const response = await makeJsonApiCall('/api/2fa/setup/begin', 'POST', {})
      if (response.ok) {
        const data = await response.json()
        setTfaSecret(data.secret)
        setTfaQRCode(data.qrCode)
        setSetupStep('2fa-setup')
      } else {
        throw new Error('Failed to setup 2FA')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to setup 2FA')
    } finally {
      setSettingUp(false)
    }
  }

  const handleVerify2FASetup = async () => {
    try {
      setSettingUp(true)
      setError(null)
      const response = await makeJsonApiCall('/api/2fa/setup/verify', 'POST', { token: tfaToken })
      if (response.ok) {
        const data = await response.json()
        setTfaBackupCodes(data.backupCodes)
        setSetupStep('2fa-verify')
      } else {
        const data = await response.json()
        throw new Error(data.error || 'Invalid token')
      }
    } catch (err: any) {
      setError(err.message || 'Verification failed')
    } finally {
      setSettingUp(false)
    }
  }

  const complete2FASetup = async () => {
    setTfaBackupCodes([])
    setTfaToken('')
    setTfaSecret('')
    setTfaQRCode('')
    // Show success screen instead of closing immediately
    setSetupComplete(true)
    setSetupStep('success')
    // Refresh security features
    await checkSecurityFeatures()
  }
  
  const handleContinueToDashboard = () => {
    onVerified()
  }
  
  const handleAddAnotherFeature = () => {
    setSetupStep('choose')
    setSetupComplete(false)
    setError(null)
  }

  const handleSkip = async () => {
    try {
      // Call backend to record skip
      const response = await makeJsonApiCall('/api/auth/security-setup/skip', 'POST', {})
      
      if (response.ok) {
        // Store skip timestamp for 7 days
        const skipUntil = new Date()
        skipUntil.setDate(skipUntil.getDate() + 7)
        localStorage.setItem('securitySkipUntil', skipUntil.toISOString())
        
        if (onSkip) {
          onSkip()
        }
      } else {
        throw new Error('Failed to skip security setup')
      }
    } catch (err: any) {
      console.error('Error skipping security setup:', err)
      // Fallback to local storage only
      const skipUntil = new Date()
      skipUntil.setDate(skipUntil.getDate() + 7)
      localStorage.setItem('securitySkipUntil', skipUntil.toISOString())
      
      if (onSkip) {
        onSkip()
      }
    }
  }

  if (!isOpen) return null

  const hasAnySecurityFeature = hasPasskeys || has2FA || hasSMS2FA

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-6">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75" aria-hidden="true"></div>

        {/* Modal panel */}
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
          <div className="px-6 pt-6 pb-6">
            {/* Icon at top */}
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 rounded-full bg-gray-900 dark:bg-gray-100 flex items-center justify-center shadow-lg">
                <svg className="h-8 w-8 text-white dark:text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
            </div>
            
            {/* Close/Skip/Cancel button */}
            {!setupComplete && (
              <>
                {!sessionToken && onSkip && (
                  <button
                    onClick={handleSkip}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
                    aria-label="Skip for now"
                    type="button"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
                {sessionToken && onCancel && (
                  <button
                    onClick={onCancel}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
                    aria-label={t('security.cancelVerification')}
                    type="button"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </>
            )}
            
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {loading ? t('security.checkingSecurity') : setupComplete ? t('security.setupSuccessful') || 'Security Feature Added!' : hasAnySecurityFeature ? t('security.verificationRequired') : t('security.secureYourAccount')}
              </h3>
                <div className="mt-2">
                  {loading ? (
                    <div className="text-center py-4">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
                    </div>
                  ) : hasAnySecurityFeature ? (
                    // VERIFICATION FLOW
                    <div className="space-y-4">
                      {!selectedMethod ? (
                        <>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {t('security.chooseVerificationMethod')}
                          </p>
                          <div className="space-y-2">
                            {hasPasskeys && (
                              <button
                                onClick={() => setSelectedMethod('passkey')}
                                className="w-full p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-gray-900 dark:hover:border-gray-100 transition-colors text-left"
                              >
                                <div className="flex items-center">
                                  <div className="flex-shrink-0">
                                    <svg className="h-6 w-6 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                  <div className="ml-3">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">{t('security.passkey')}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('security.passkeyVerifyDescription')}</p>
                                  </div>
                                </div>
                              </button>
                            )}
                            {has2FA && (
                              <button
                                onClick={() => setSelectedMethod('2fa')}
                                className="w-full p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-gray-900 dark:hover:border-gray-100 transition-colors text-left"
                              >
                                <div className="flex items-center">
                                  <div className="flex-shrink-0">
                                    <svg className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                    </svg>
                                  </div>
                                  <div className="ml-3">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">{t('security.authenticatorApp')}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('security.enter6DigitCode')}</p>
                                  </div>
                                </div>
                              </button>
                            )}
                            {hasSMS2FA && (
                              <button
                                onClick={() => {
                                  setSelectedMethod('sms2fa')
                                  handleSendSMSCode()
                                }}
                                className="w-full p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-gray-900 dark:hover:border-gray-100 transition-colors text-left"
                              >
                                <div className="flex items-center">
                                  <div className="flex-shrink-0">
                                    <svg className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                    </svg>
                                  </div>
                                  <div className="ml-3">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">{t('security.sms2faVerification')}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('security.receiveCodeViaSMS')}</p>
                                  </div>
                                </div>
                              </button>
                            )}
                          </div>
                        </>
                      ) : selectedMethod === 'passkey' ? (
                        <div className="space-y-4">
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {t('security.usePasskeyToVerify')}
                          </p>
                          {error && (
                            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                            </div>
                          )}
                          <div className="flex gap-2">
                            <button
                              onClick={() => setSelectedMethod(null)}
                              className="flex-1 px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                              {t('common.back')}
                            </button>
                            <button
                              onClick={handlePasskeyVerification}
                              disabled={verifying}
                              className="flex-1 px-4 py-2 text-sm bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-md hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50"
                            >
                              {verifying ? t('security.verifying') : t('security.verifyWithPasskey')}
                            </button>
                          </div>
                        </div>
                      ) : selectedMethod === '2fa' ? (
                        <div className="space-y-4">
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {t('security.enterCodeFromApp')}
                          </p>
                          {error && (
                            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                            </div>
                          )}
                          <input
                            type="text"
                            value={tfaToken}
                            onChange={(e) => setTfaToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="000000"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center text-lg tracking-widest font-mono focus:outline-none focus:ring-2 focus:ring-gray-900"
                            maxLength={6}
                            autoFocus
                          />
                          {/* Show "Use different method" if multiple methods available */}
                          {(hasPasskeys || hasSMS2FA) && (
                            <div className="text-center">
                              <button
                                onClick={() => {
                                  setSelectedMethod(null)
                                  setTfaToken('')
                                  setError(null)
                                }}
                                className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 underline"
                              >
                                {t('security.useDifferentMethod') || 'Use a different security method'}
                              </button>
                            </div>
                          )}
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setSelectedMethod(null)
                                setTfaToken('')
                              }}
                              className="flex-1 px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                              {t('common.back')}
                            </button>
                            <button
                              onClick={handle2FAVerification}
                              disabled={verifying || tfaToken.length !== 6}
                              className="flex-1 px-4 py-2 text-sm bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-md hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50"
                            >
                              {verifying ? t('security.verifying') : t('security.verify')}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {!smsCodeSent ? (
                            // Sending SMS code...
                            <div className="text-center py-6">
                              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100 mb-3"></div>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {t('security.sendingCode')}
                              </p>
                            </div>
                          ) : (
                            <>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {t('security.enterSMSCode')}
                              </p>
                              {error && (
                                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                                  <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                                </div>
                              )}
                              <input
                                type="text"
                                value={tfaToken}
                                onChange={(e) => setTfaToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                placeholder="000000"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center text-lg tracking-widest font-mono focus:outline-none focus:ring-2 focus:ring-gray-900"
                                maxLength={6}
                                autoFocus
                              />
                              <div className="text-center space-y-2">
                                <button
                                  onClick={handleResendSMS}
                                  disabled={!canResendSMS}
                                  className="text-sm text-gray-900 dark:text-gray-600 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {canResendSMS ? t('security.resendCode') : t('security.resendIn', { seconds: resendTimer })}
                                </button>
                                {/* Show "Use different method" if multiple methods available */}
                                {(hasPasskeys || has2FA) && (
                                  <div>
                                    <button
                                      onClick={() => {
                                        setSelectedMethod(null)
                                        setSmsCodeSent(false)
                                        setTfaToken('')
                                        setError(null)
                                      }}
                                      className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 underline"
                                    >
                                      {t('security.useDifferentMethod') || 'Use a different security method'}
                                    </button>
                                  </div>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => {
                                    setSelectedMethod(null)
                                    setSmsCodeSent(false)
                                    setTfaToken('')
                                  }}
                                  className="flex-1 px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                  {t('common.back')}
                                </button>
                                <button
                                  onClick={handleSMS2FAVerification}
                                  disabled={verifying || tfaToken.length !== 6}
                                  className="flex-1 px-4 py-2 text-sm bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-md hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50"
                                >
                                  {verifying ? t('security.verifying') : t('security.verify')}
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    // SETUP FLOW
                    <div className="space-y-4">
                      {setupStep === 'choose' ? (
                        <>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {t('security.setupSecurityMessage')}
                          </p>
                          <div className="space-y-2">
                            {supportsPasskeys && (
                              <button
                                onClick={handleSetupPasskey}
                                disabled={settingUp}
                                className="w-full p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-gray-900 dark:hover:border-gray-100 transition-colors text-left disabled:opacity-50"
                              >
                                <div className="flex items-center">
                                  <div className="flex-shrink-0">
                                    <svg className="h-6 w-6 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                  <div className="ml-3">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">{t('security.passkeyRecommended')}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('security.passkeySetupDescription')}</p>
                                  </div>
                                </div>
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setSetupStep('2fa-setup')
                                handleBegin2FASetup()
                              }}
                              disabled={settingUp}
                              className="w-full p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-gray-900 dark:hover:border-gray-100 transition-colors text-left disabled:opacity-50"
                            >
                              <div className="flex items-center">
                                <div className="flex-shrink-0">
                                  <svg className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                  </svg>
                                </div>
                                <div className="ml-3">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">{t('security.authenticatorApp')}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('security.authenticatorDescription')}</p>
                                </div>
                              </div>
                            </button>
                          </div>
                          {error && (
                            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                            </div>
                          )}
                          {onSkip && (
                            <button
                              onClick={handleSkip}
                              className="w-full px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 underline"
                            >
                              {t('security.skipFor7Days')}
                            </button>
                          )}
                        </>
                      ) : setupStep === '2fa-setup' ? (
                        <div className="space-y-4">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{t('security.scanQRCode')}</p>
                          {tfaQRCode && (
                            <div className="bg-white p-4 rounded-lg inline-block mx-auto">
                              <img src={tfaQRCode} alt="2FA QR Code" className="w-48 h-48" />
                            </div>
                          )}
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t('security.orManualSecret')}</p>
                          <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600 font-mono text-sm break-all">
                            {tfaSecret}
                          </div>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                            {t('security.enterCodeToVerify')}
                          </p>
                          <input
                            type="text"
                            value={tfaToken}
                            onChange={(e) => setTfaToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="000000"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center text-lg tracking-widest font-mono focus:outline-none focus:ring-2 focus:ring-gray-900"
                            maxLength={6}
                          />
                          {error && (
                            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                            </div>
                          )}
                          <button
                            onClick={handleVerify2FASetup}
                            disabled={settingUp || tfaToken.length !== 6}
                            className="w-full px-4 py-2 text-sm bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-md hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50"
                          >
                            {settingUp ? t('security.verifying') : t('security.verifyAndEnable2FA')}
                          </button>
                        </div>
                      ) : setupStep === '2fa-verify' ? (
                        <div className="space-y-4">
                          <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                            <p className="text-sm text-green-800 dark:text-green-200 font-medium mb-2">
                              ✓ {t('security.2FAEnabledSuccess')}
                            </p>
                            <p className="text-xs text-green-700 dark:text-green-300 mb-3">
                              {t('security.saveBackupCodes')}
                            </p>
                            <div className="bg-white dark:bg-gray-800 p-3 rounded border border-gray-300 dark:border-gray-600">
                              <div className="grid grid-cols-2 gap-2 font-mono text-xs">
                                {tfaBackupCodes.map((code, idx) => (
                                  <div key={idx} className="text-gray-900 dark:text-white">
                                    {code}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={complete2FASetup}
                            className="w-full px-4 py-2 text-sm bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-md hover:bg-gray-800 dark:hover:bg-gray-200"
                          >
                            {t('common.continue') || 'Continue'}
                          </button>
                        </div>
                      ) : (
                        // SUCCESS SCREEN
                        <div className="space-y-4">
                          <div className="text-center py-4">
                            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
                              <svg className="h-10 w-10 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <p className="text-base text-gray-700 dark:text-gray-300 mb-6">
                              {t('security.featureAddedMessage') || 'Your account is now more secure!'}
                            </p>
                          </div>
                          
                          <div className="bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-4">
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                              💡 {t('security.recommendMultipleFeatures') || 'For maximum security, consider adding another authentication method.'}
                            </p>
                          </div>

                          <div className="space-y-3">
                            <button
                              onClick={handleContinueToDashboard}
                              className="w-full px-4 py-3 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg font-semibold shadow-lg hover:bg-gray-800 dark:hover:bg-gray-200 hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                            >
                              {t('security.continueToDashboard') || 'Continue to Dashboard'}
                            </button>
                            
                            <button
                              onClick={handleAddAnotherFeature}
                              className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                              {t('security.addAnotherFeature') || 'Add Another Security Feature'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SecurityVerificationModal
